import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  AfterViewInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subject, takeUntil, timeout, catchError, of, switchMap, finalize } from 'rxjs';
import { EventDTO } from '../../../../dto/event/event.dto';
import { DunaEventsScheduleComponent } from '../../duna-events-schedule/duna-events-schedule.component';
import {
  calculateScheduleViewDateRange,
  ScheduleViewName,
} from '../../duna-events-schedule/events-schedule-date-range.util';
import { DashboardEventsCalendarLoaderService } from '../../../../services/dashboard/dashboard-events-calendar-loader.service';
import {
  dashboardFiltersToCriteria,
  getScheduleViewFromFilters,
  getSelectedDateFromFilters,
} from '../../../../services/dashboard/dashboard-events-filter.mapper';
import { ComponentDataSourceService } from '../../../../services/component-data-source/component-data-source.service';
import {
  EventCategoryColorsMap,
  normalizeEventCategoryColors,
} from '../../../../services/component-data-source/data/event/event-category-color.util';
import { AuthService } from '../../../../services/auth/auth.service';
import { take } from 'rxjs';

@Component({
  selector: 'app-duna-dashboard-events-calendar-widget',
  standalone: true,
  imports: [CommonModule, DunaEventsScheduleComponent, MatButtonModule, MatIconModule],
  templateUrl: './duna-dashboard-events-calendar-widget.component.html',
  styleUrl: './duna-dashboard-events-calendar-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DunaDashboardEventsCalendarWidgetComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() filters: Record<string, unknown> = {};
  @Input() reloadToken = 0;

  @Output() filtersChange = new EventEmitter<Record<string, unknown>>();

  @ViewChild('fullscreenTemplate') fullscreenTemplate?: TemplateRef<unknown>;
  @ViewChild('inlineSchedule') inlineSchedule?: DunaEventsScheduleComponent;

  events: EventDTO[] = [];
  loading = false;
  scheduleView: ScheduleViewName = 'Month';
  selectedDate = new Date();
  categoryColors: EventCategoryColorsMap = {};

  private destroy$ = new Subject<void>();
  private overlayRef: OverlayRef | null = null;
  private lastRange: { startDate: string; endDate: string } | null = null;
  private fetchRange$ = new Subject<{ startDate: string; endDate: string }>();

  constructor(
    private loader: DashboardEventsCalendarLoaderService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private dataSourceService: ComponentDataSourceService,
    private authService: AuthService
  ) {
    this.loadCategoryColors();
    this.fetchRange$
      .pipe(
        switchMap((range) => {
          this.loading = true;
          this.cdr.markForCheck();
          const criteria = dashboardFiltersToCriteria(this.filters);
          return this.loader.loadEvents(criteria, range).pipe(
            timeout(30000),
            catchError(() => of([] as EventDTO[])),
            finalize(() => {
              this.loading = false;
              this.cdr.markForCheck();
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((events) => {
        this.events = events;
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    this.ensureEventsLoaded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters']) {
      this.scheduleView = getScheduleViewFromFilters(this.filters);
      this.selectedDate = getSelectedDateFromFilters(this.filters);
    }
    if (changes['filters'] || changes['reloadToken']) {
      this.ensureEventsLoaded();
    }
  }

  ngOnDestroy(): void {
    this.closeFullscreen();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.overlayRef) {
      this.closeFullscreen();
    }
  }

  onVisibleRangeChange(range: { startDate: string; endDate: string }): void {
    this.lastRange = range;
    this.fetchEvents(range);
  }

  onScheduleViewChange(view: ScheduleViewName): void {
    this.patchFilters({ scheduleView: view });
  }

  onSelectedDateChange(date: Date): void {
    this.patchFilters({ selectedDateIso: date.toISOString() });
  }

  onEventClick(event: EventDTO): void {
    if (event?.guid) {
      this.router.navigate(['/event', event.guid]);
    }
  }

  openFullscreen(): void {
    if (!this.fullscreenTemplate || this.overlayRef) return;
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'dashboard-calendar-fullscreen-backdrop',
      panelClass: 'dashboard-events-calendar-fullscreen',
      width: '100vw',
      height: '100vh',
      positionStrategy: this.overlay.position().global().top('0').left('0'),
    });
    this.overlayRef.attach(
      new TemplatePortal(this.fullscreenTemplate, this.viewContainerRef)
    );
    this.overlayRef.backdropClick().subscribe(() => this.closeFullscreen());
  }

  closeFullscreen(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private patchFilters(partial: Record<string, unknown>): void {
    this.filtersChange.emit({ ...this.filters, ...partial });
  }

  private ensureEventsLoaded(): void {
    const range =
      this.lastRange ??
      calculateScheduleViewDateRange(this.selectedDate, this.scheduleView);
    this.lastRange = range;
    this.fetchEvents(range);
  }

  private fetchEvents(range: { startDate: string; endDate: string }): void {
    this.lastRange = range;
    this.fetchRange$.next(range);
  }

  private loadCategoryColors(): void {
    const user = this.authService.getCurrentUser() as Record<string, unknown> | null;
    const orgId =
      user?.['organizations_id'] ??
      (user?.['user_info'] as Record<string, unknown> | undefined)?.['organizations_id'];
    const n = typeof orgId === 'number' ? orgId : parseInt(String(orgId ?? ''), 10);
    if (!Number.isFinite(n) || n <= 0) return;
    const obs = this.dataSourceService.executeDataSource('org_settings', {
      organizationId: n,
    });
    if (!obs) return;
    obs.pipe(take(1), takeUntil(this.destroy$)).subscribe((res: { data?: Record<string, unknown> }) => {
      this.categoryColors = normalizeEventCategoryColors(
        res?.data?.['event_category_colors']
      );
      this.cdr.markForCheck();
    });
  }
}
